import { useMemo, useState } from 'react';
import { useI18n } from '../../i18n/I18nProvider';
import { VirtualTree } from '../../components/tree/VirtualTree';
import {
  accountName,
  creationRefusal,
  expandableCodes,
  filterTree,
  totalAccounts,
} from './accounts.api';
import type { AccountNode, AsyncVoid, CreateAccountInput } from './accounts.api';
import './accounts.css';

export interface AccountsViewProps {
  tree: readonly AccountNode[];
  onImportRequest?: () => void;
  onCreate?: AsyncVoid<CreateAccountInput>;
  busy?: boolean;
}

export function AccountsView({ tree, onImportRequest, onCreate, busy }: AccountsViewProps): JSX.Element {
  const { t, language } = useI18n();
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set(expandableCodes(tree)));
  const [parent, setParent] = useState<AccountNode | null>(null);
  const visible = useMemo(() => filterTree(tree, query), [tree, query]);
  const total = totalAccounts(tree);

  const toggle = (code: string): void => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  return (
    <div className="accounts">
      <header className="page-head accounts__head">
        <div>
          <h1>{t('accounts.title')}</h1>
          <p className="muted">
            <span data-testid="accounts-total">{String(total)}</span> {t('accounts.accountsSuffix')}
          </p>
        </div>
        <div className="accounts__actions">
          <button type="button" className="btn" onClick={onImportRequest} disabled={busy}>
            {t('accounts.import')}
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => {
              const root = tree.find((node) => node.isGroup);
              if (root !== undefined) setParent(root);
            }}
            disabled={busy}
          >
            {t('accounts.newAccount')}
          </button>
        </div>
      </header>

      <div className="accounts__toolbar">
        <input
          className="input"
          type="search"
          value={query}
          placeholder={t('accounts.searchPlaceholder')}
          aria-label={t('accounts.searchPlaceholder')}
          onChange={(event) => setQuery(event.currentTarget.value)}
        />
        <span className="pill">
          {t('accounts.offbalanceFilter')}: {String(visible.length)}
        </span>
      </div>

      <VirtualTree
        roots={visible}
        expanded={expanded}
        viewportHeight={520}
        rowHeight={38}
        emptyLabel={t('accounts.empty')}
        onToggle={toggle}
        renderRow={(node) => (
          <>
            <span className="accounts__code mono">{node.code}</span>
            <span className="accounts__name">{accountName(node, language)}</span>
            {node.isGroup ? <span className="pill">{t('accounts.group')}</span> : null}
            {node.isOffbalance ? (
              <span className="pill pill--warn" data-testid="offbalance-marker">
                {t('accounts.offbalance')}
              </span>
            ) : null}
            {node.kind === 'P' || node.kind === 'AP' ? <span className="pill">{t('accounts.passive')}</span> : null}
            <span className="accounts__dims">
              {node.dimensionRules.length === 0
                ? t('accounts.noDimensions')
                : node.dimensionRules
                    .map((rule) => `${rule.dimType}${rule.required ? '*' : ''}`)
                    .join(', ')}
            </span>
            {creationRefusal(node) === null && onCreate !== undefined ? (
              <button type="button" className="btn btn--small" onClick={() => setParent(node)}>
                {t('accounts.addChild')}
              </button>
            ) : null}
          </>
        )}
      />

      {parent === null || onCreate === undefined ? null : (
        <CreateAccountForm
          parent={parent}
          busy={busy === true}
          onCancel={() => setParent(null)}
          onSubmit={async (input) => {
            await onCreate(input);
            setParent(null);
          }}
        />
      )}
    </div>
  );
}

function CreateAccountForm({
  parent,
  busy,
  onCancel,
  onSubmit,
}: {
  parent: AccountNode;
  busy: boolean;
  onCancel: () => void;
  onSubmit: AsyncVoid<CreateAccountInput>;
}): JSX.Element {
  const { t } = useI18n();
  const [code, setCode] = useState('');
  const [nameAz, setNameAz] = useState('');
  const [error, setError] = useState<string | null>(null);
  const refusal = creationRefusal(parent);

  const submit = async (): Promise<void> => {
    if (refusal !== null) {
      setError(refusal);
      return;
    }
    try {
      await onSubmit({ code, parentCode: parent.code, nameI18n: { az: nameAz, en: nameAz, ru: nameAz } });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  };

  return (
    <div className="accounts__dialog" role="dialog" aria-modal="true" data-testid="create-account">
      <header className="accounts__dialog-head">
        <h2>{t('accounts.createTitle')}</h2>
        <button type="button" className="btn btn--small" onClick={onCancel}>
          {t('accounts.close')}
        </button>
      </header>
      <p className="muted">
        {t('accounts.parent')}: <span className="mono">{parent.code}</span>
      </p>
      {error === null ? null : (
        <p className="alert alert--danger" role="alert">
          {error}
        </p>
      )}
      <label className="field">
        <span>{t('accounts.code')}</span>
        <input className="input" value={code} onChange={(event) => setCode(event.currentTarget.value)} />
      </label>
      <label className="field">
        <span>{t('accounts.name')}</span>
        <input className="input" value={nameAz} onChange={(event) => setNameAz(event.currentTarget.value)} />
      </label>
      <button type="button" className="btn btn--primary" disabled={busy || refusal !== null} onClick={() => void submit()}>
        {t('accounts.create')}
      </button>
    </div>
  );
}
